/**
 * Cursors, the page's half: what this page tells the server about its own
 * pointer. Pure functions only; the pointer events, the socket and the canvas
 * are elsewhere.
 *
 * The Cell under the pointer goes up when it changes, at most 20 a second, the
 * latest Cell winning, and -1 when the pointer leaves the Grid. The server
 * drops a cursor on its own when the tab hides or the socket closes, and holds
 * none for an Onlooker, so each of those is `forgotten`: the page then says
 * where its pointer is again, even if it never moved.
 *
 * The server sends everyone the same whole set, this page's own cursor
 * included, and the page leaves its own out.
 */

import type { CursorSet } from "./wire.ts"

/** The fastest the page sends `cursor`: 20 a second, the server's own tick. */
export const CURSOR_GAP_MS = 50

export interface CursorOut {
  /** The Cell under the pointer, or -1 off the Grid. */
  readonly wanted: number
  /** What the server holds for this page, as far as the page knows. */
  readonly sent: number
  /** The page time of the last send. */
  readonly at: number
}

export const cursorOut: CursorOut = { wanted: -1, sent: -1, at: -Infinity }

/** The pointer is over this Cell now, or -1 for off the Grid. */
export const pointAt = (out: CursorOut, cell: number): CursorOut =>
  out.wanted === cell ? out : { ...out, wanted: cell }

/** What goes up now, if anything, and the state after it. */
export const take = (
  out: CursorOut,
  now: number
): { readonly out: CursorOut; readonly send: number | undefined } =>
  out.wanted === out.sent || now - out.at < CURSOR_GAP_MS
    ? { out, send: undefined }
    : { out: { ...out, sent: out.wanted, at: now }, send: out.wanted }

/** The server holds no cursor for this page. The pointer may still be where it was. */
export const forgotten = (out: CursorOut): CursorOut => ({ ...out, sent: -1 })

/** Everyone's cursor but this page's own, `you` being its connection id. */
export const othersOf = (set: CursorSet, you: number): CursorSet => {
  const own = String(you)
  if (!(own in set)) return set
  return Object.fromEntries(Object.entries(set).filter(([id]) => id !== own))
}
